import React, { forwardRef } from "react";
import { Box, Button, Grid, IconButton, TextField, Typography, useTheme } from "@mui/material";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import { isEqual } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import AutocompleteDropdown from "../../../../../../../../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import { CountriesApiService } from "../../../../../../../../../services/countries/countries-api.service";
import { TravelExpenseTypesApiService } from "../../../../../../../../../services/travel-expense-types/travel-expense-types-api.service";
import { TextFieldTypeAmount } from "../../../../../../../../../reusable-components/Table/AddEditItem/TextFieldTypeAmount/TextFieldTypeAmount";
import { CurrenciesApiService } from "../../../../../../../../../services/currencies/currencies-api.service";
import { TravelExpenseRef } from "../../../../../../../../../models/travel-expenses";
import AttachedDocuments from "../../../../../../../../../containers/AttachedDocuments/AttachedDocuments";
import { DOCUMENT_CATEGORIES, DOCUMENT_TYPES } from "../../../../../../../../../utils/constants/constants";
import { DocumentCategoryRef } from "../../../../../../../../../models/document-categories";
import { DocumentTypeRef } from "../../../../../../../../../models/document-types";
import AddNewDocument from "../../../../../../../../../containers/AddNewDocument/AddNewDocument";
import { EmployeeRef } from "../../../../../../../../../models/employees";
import { DocumentDTO } from "../../../../../../../../../models/documents";
import { DateContainer } from "./styles";
import { DatePickerWrapper } from "../../../../../../../../../containers/FilterByYear/styles";

interface Props {
  travelExpense: TravelExpenseRef;
  handleChange: (field: keyof TravelExpenseRef, value: TravelExpenseRef[keyof TravelExpenseRef]) => void;
  readOnly: boolean;
  employee: EmployeeRef;
  minDate?: Date;
  maxDate?: Date;
  handleNotifyOnDocumentSave: (document: DocumentDTO) => void;
  exchangeRateSuggestions: React.ReactNode;
  openCCCDDialog: () => void;
}

const AddReceiptFields = ({
  handleChange,
  readOnly,
  travelExpense,
  employee,
  minDate,
  maxDate,
  handleNotifyOnDocumentSave,
  exchangeRateSuggestions,
  openCCCDDialog,
}: Props) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const tRP = "components.add_edit_travel_expense";

  const getCurrentLanguage = (): string => (i18n.language === "en" ? "enGB" : i18n.language);

  const CustomInput = forwardRef(
    (
      {
        id,
        value,
        onClick,
      }: {
        id: string;
        value: string;
        onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
      },
      _ref: any
    ) => (
      <Button
        variant="outlined"
        onClick={onClick}
        ref={_ref}
      >
        {value}
      </Button>
    )
  );

  return (
    <>
      {/* <Box> */}
      {!!travelExpense.receiptsDocument ? (
        <Grid sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <Typography style={{ fontSize: "0.8rem" }}>Receipt document : </Typography>
          <AttachedDocuments
            count={travelExpense.receiptsDocument?.filesCount}
            documentIDs={[travelExpense.receiptsDocument?.id]}
            buttonSize="2x"
            documentsReadOnlyFields={[
              {
                documentID: travelExpense.receiptsDocument.id,
                readOnlyFields: {
                  isEmployeeRO: true,
                  isCategoryRO: true,
                  isLegalEntityRO: true,
                  isTypeRO: true,
                  isDateRO: true,
                  isStatusRO: true,
                },
              },
            ]}
            hideReadOnlyFields
            maxFilesNumber={1}
            useSimpleView
            handleReturnDocumentToParent={handleNotifyOnDocumentSave}
            documentsNames={[travelExpense.receiptsDocument?.name]}
            documentIsReadOnly={readOnly}
          />
          <IconButton
            style={{ fontSize: "0.8rem" }}
            onClick={openCCCDDialog}
          >
            <FontAwesomeIcon icon={faClose} />
          </IconButton>
        </Grid>
      ) : (
        <>
          {!readOnly && (
            <AddNewDocument
              buttonType="text"
              openButtonKey={t(`${tRP}.add_new_document`)}
              handleNotifyParent={(document) => {
                document && handleNotifyOnDocumentSave(document);
              }}
              customLinkStyle={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
              }}
              displayExpandableItemUponSave={false}
              documentFieldsProps={{
                legalEntity: travelExpense.legalEntity,
                employee: employee,
                documentDate: new Date(),
                category: DOCUMENT_CATEGORIES.ACCOUNTING_INPUT as DocumentCategoryRef,
                type: DOCUMENT_TYPES.TravelExpensesReceipt as DocumentTypeRef,
              }}
              readOnlyFields={{
                isEmployeeRO: true,
                isCategoryRO: true,
                isLegalEntityRO: true,
                isTypeRO: true,
                isDateRO: true,
              }}
              hideReadOnlyFields
              maxFilesNumber={1}
            />
          )}
        </>
      )}
      <DateContainer>
        <Typography>{`${t(`${tRP}.fields.date`)}`}</Typography>
        <DatePickerWrapper>
          <DatePicker
            onChange={(value) => {
              !!value && handleChange("date", value);
            }}
            customInput={React.createElement(CustomInput)}
            dateFormat="yyyy MMMM dd"
            locale={getCurrentLanguage()}
            popperPlacement="auto"
            selected={new Date(travelExpense.date)}
            minDate={minDate ?? null}
            maxDate={maxDate ?? null}
            disabled={readOnly}
          />
        </DatePickerWrapper>
      </DateContainer>
      <AutocompleteDropdown
        serviceApi={new TravelExpenseTypesApiService()}
        field={"type"}
        labelKey={`${t(`${tRP}.fields.type`)}`}
        handleChange={handleChange}
        selectedValue={travelExpense.type}
        disabled={readOnly}
        translationRoutePath="travel_expense_types"
        required
      />

      {/* </Box> */}

      {/* <Box> */}

      {/* </Box> */}

      {/* <Box> */}
      {/* {travelExpense?.type?.includeDescription && ( */}
      <TextField
        style={{ width: "100%" }}
        label={`${t(`${tRP}.fields.description`)}`}
        value={travelExpense.description ?? ""}
        multiline
        maxRows={2}
        onChange={(event) => {
          handleChange("description", event.currentTarget.value);
        }}
        disabled={readOnly}
        required
      />
      {/* )} */}
      <AutocompleteDropdown
        labelKey={`${t(`${tRP}.fields.country`)}`}
        field={"country"}
        serviceApi={new CountriesApiService()}
        handleChange={handleChange}
        selectedValue={travelExpense.country}
        disabled={readOnly}
        required
      />
      {travelExpense?.type?.includeLocation && (
        <TextField
          style={{ width: "100%" }}
          label={`${t(`${tRP}.fields.location`)}`}
          value={travelExpense.location ?? ""}
          onChange={(event) => {
            handleChange("location", event.currentTarget.value);
          }}
          disabled={readOnly}
        />
      )}
      <TextFieldTypeAmount
        field={"amount"}
        labelKey={`${t(`${tRP}.fields.amount`)}`}
        amountValue={travelExpense.amount ?? 0}
        handleChange={(_, number) => handleChange("amount", number)}
        endAdornment={
          <AutocompleteDropdown
            labelKey={`${t(`${tRP}.fields.currency`)}`}
            field={"currency"}
            handleChange={handleChange}
            serviceApi={new CurrenciesApiService()}
            selectedValue={travelExpense.currency}
            inputStyle={{
              padding: "1px 46px 1px 1px",
              width: "100px",
            }}
            fieldProp={"code"}
          />
        }
        required
        disabled={readOnly}
      />
      <TextField
        label={`${t(`${tRP}.fields.exchange_rate`)}`}
        type="number"
        onChange={(event) => {
          handleChange("exchangeRate", event.currentTarget.value);
        }}
        value={travelExpense.exchangeRate}
        disabled={isEqual(travelExpense.currency?.id, travelExpense.legalEntity?.defaultCurrency?.id) || readOnly}
        InputProps={{
          endAdornment: <>{exchangeRateSuggestions}</>,
        }}
      />
      <TextFieldTypeAmount
        field={"convertedAmount"}
        labelKey={`${t(`${tRP}.fields.converted_amount`)}`}
        amountValue={travelExpense.convertedAmount ?? 0}
        handleChange={(_, number) => handleChange("convertedAmount", number)}
        disabled={true}
        endAdornment={
          <TextField
            label={`${t(`${tRP}.fields.currency`)}`}
            value={travelExpense.convertedAmountCurrency?.code}
            disabled={true}
            inputProps={{
              style: {
                padding: "8px",
                width: "80px",
              },
            }}
          />
        }
      />
      {travelExpense?.type?.includeClientName && (
        <TextField
          style={{ width: "100%" }}
          label={`${t(`${tRP}.fields.client_name`)}`}
          value={travelExpense.clientName ?? ""}
          multiline
          maxRows={2}
          onChange={(event) => {
            handleChange("clientName", event.currentTarget.value);
          }}
          disabled={readOnly}
        />
      )}
      {travelExpense?.type?.includeGuestNames && (
        <TextField
          style={{ width: "100%" }}
          label={`${t(`${tRP}.fields.guest_names`)}`}
          value={travelExpense.guestNames ?? ""}
          multiline
          maxRows={2}
          onChange={(event) => {
            handleChange("guestNames", event.currentTarget.value);
          }}
          disabled={readOnly}
        />
      )}
      {/* </Box> */}

      {/* <Box> */}

      {travelExpense?.type?.includeVatRate && (
        <TextField
          label={`${t(`${tRP}.fields.vat_rate`)}`}
          type="number"
          value={travelExpense.vatRate}
          onChange={(e) => {
            if (!!Number(e.target.value || Number(e.target.value) === 0)) {
              Number(e.target.value) >= 0 && Number(e.target.value) <= 100 && handleChange("vatRate", e.target.value);
            } else {
              handleChange("vatRate", e.target.value);
            }
          }}
          InputProps={{
            inputProps: { min: 0, max: 100, step: 0.1 },
          }}
          onBlur={(e) => {
            e.target.value === "" && handleChange("vatRate", 0);
          }}
          disabled={readOnly}
        />
      )}
      {/* </Box> */}

      {/* <Box> */}

      {travelExpense?.type?.includeVatAmount && (
        <TextFieldTypeAmount
          field={"vatAmount"}
          labelKey={`${t(`${tRP}.fields.vat_amount`)}`}
          amountValue={travelExpense.vatAmount ?? 0}
          handleChange={(_, number) => handleChange("vatAmount", number)}
          disabled={readOnly}
        />
      )}
    </>
  );
};

export default AddReceiptFields;
